import { zodResolver } from '@hookform/resolvers/zod'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { EmailIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { forgotPassword } from '../../api/auth/forgot-password'
import TranslatedForm, {
  FormControl,
  FormField,
  FormItem
} from '../../components/elements/forms/form'
import ControlledInput from '../../components/elements/inputs/controlled-input'
import AuthLayout from '../../components/layouts/auth-layout'
import { Paths } from '../../lib/constants/path'
import useTranslate from '../../lib/hooks/use-translate'
import { ForgotPasswordInput } from '../../lib/types/models/auth/forgot-password'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { createStringSchema } from '../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../utils/constants/form.constants'

export default function ForgotPasswordPage() {
  const { navigate } = useNavigator()
  const breakpoint = useBreakPoint()
  const { t } = useTranslate()

  const isMobile = breakpoint === 'mobile'

  const formSchema: ZodType<ForgotPasswordInput> = z.object({
    email: createStringSchema('email', {
      isEmail: true,
      errorMessage: t('errors.string.email')
    })
  })

  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: ''
    },
    mode: FORM_MODE
  })

  const { mutate, isPending } = useMutation({
    mutationFn: forgotPassword,
    onSuccess: () => {
      navigate(Paths.AUTH_RESET_PASSWORD_PAGE)
    },
    onError: showAxiosError
  })

  function onSubmit(values: ForgotPasswordInput) {
    mutate(values)
  }

  useEffect(() => {
    document.querySelector('html')?.setAttribute('data-theme', 'admin')
  }, [])

  return (
    <AuthLayout text={t('forgot.password.title')}>
      <Typography variant="h1" className="mb-6">
        {t('forgot.password.subtitle')}
      </Typography>
      <TranslatedForm<ForgotPasswordInput> form={form}>
        <form onSubmit={form.handleSubmit(onSubmit)} autoComplete="off">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput
                    type="email"
                    placeholder="E-mail"
                    label={isMobile ? undefined : 'E-mail'}
                    icon={EmailIcon}
                    wrapperClassName="bg-white"
                    autoComplete="off"
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <Button
            loading={isPending}
            type="submit"
            label={t('button.continue')}
            className="w-full mt-5 mobile:bg-blue-dark-active"
          />
        </form>
      </TranslatedForm>
    </AuthLayout>
  )
}
